import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Alert,
  Typography,
  Button,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import AirIcon from '@mui/icons-material/Air';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MedicationIcon from '@mui/icons-material/Medication';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import PregnantWomanIcon from '@mui/icons-material/PregnantWoman';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import AiCardBase from './AiCardBase';
import DynamicFieldRenderer, {
  shouldShowField,
  getFieldGridColumns,
  createDefaultListItem,
} from './DynamicFieldRenderer';
import ReadOnlyDataSection from './ReadOnlyDataSection';

/**
 * Return an appropriate MUI icon for each cardType
 */
const getCardIcon = (cardType = '') => {
  const type = cardType.toLowerCase();
  if (type.includes('bp') || type.includes('pulse')) {
    return <FavoriteIcon sx={{ fontSize: 20 }} />;
  }
  if (type.includes('spo2')) {
    return <AirIcon sx={{ fontSize: 20 }} />;
  }
  if (type.includes('weight')) {
    return <MonitorWeightIcon sx={{ fontSize: 20 }} />;
  }
  if (type.includes('observation')) {
    return <DescriptionIcon sx={{ fontSize: 20 }} />;
  }
  if (type.includes('pregnancyconfirmation')) {
    return <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />;
  }
  if (type.includes('profile')) {
    return <PersonOutlineIcon sx={{ fontSize: 20 }} />;
  }
  if (type.includes('medicalhistory')) {
    return <MedicationIcon sx={{ fontSize: 20 }} />;
  }
  if (type.includes('diseasehistory')) {
    return <LocalHospitalIcon sx={{ fontSize: 20 }} />;
  }
  if (type.includes('familyhistory')) {
    return <FamilyRestroomIcon sx={{ fontSize: 20 }} />;
  }
  if (type.includes('currentpregnancy')) {
    return <PregnantWomanIcon sx={{ fontSize: 20 }} />;
  }
  if (type.includes('gtpla') || type.includes('history')) {
    return <ChildCareIcon sx={{ fontSize: 20 }} />;
  }
  return <AutoAwesomeIcon sx={{ fontSize: 20 }} />;
};

/**
 * Build initial formData from card fields
 */
const buildInitialFormData = (fields = []) => {
  const data = {};
  fields.forEach((field) => {
    if (field.value !== undefined) {
      data[field.key] = field.value;
    } else if (field.defaultValue !== undefined) {
      data[field.key] = field.defaultValue;
    } else if (field.type === 'list' || field.type === 'multiselect' || field.type === 'multitree') {
      data[field.key] = [];
    } else if (field.type === 'boolean') {
      data[field.key] = false;
    } else if (field.type === 'group' && Array.isArray(field.fields)) {
      data[field.key] = createDefaultListItem(field.fields);
    } else {
      data[field.key] = null;
    }
  });
  return data;
};

const DynamicAiCard = ({
  card = {},
  onConfirm,
  isConfirmed = false,
  isConfirming = false,
  onFormChange,
  onSaveDraft,
}) => {
  const {
    cardType = 'generic',
    label,
    isEditedByDoctor = false,
    draftSavedAt = null,
    fields = [],
    readOnlyData = null,
  } = card;

  const [formData, setFormData] = useState(() => buildInitialFormData(fields));
  const [validationError, setValidationError] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Sync state if card fields change
  useEffect(() => {
    const initial = buildInitialFormData(fields);
    setFormData(initial);
    if (onFormChange) {
      onFormChange(cardType, initial);
    }
  }, [fields, cardType]);

  const handleFieldChange = useCallback(
    (key, val) => {
      setValidationError(null);
      setFormData((prev) => {
        const next = {
          ...prev,
          [key]: val,
        };
        if (onFormChange) {
          onFormChange(cardType, next);
        }
        return next;
      });
    },
    [cardType, onFormChange]
  );

  const handleSaveDraft = async () => {
    if (!onSaveDraft) return;
    setIsSavingDraft(true);
    try {
      await onSaveDraft(cardType, formData);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleConfirm = () => {
    // Check required fields that are currently visible
    for (const field of fields) {
      if (field.required && shouldShowField(field, formData, formData)) {
        const val = formData[field.key];
        const isEmpty =
          val === null ||
          val === undefined ||
          (typeof val === 'string' && val.trim() === '') ||
          (Array.isArray(val) && val.length === 0);

        if (isEmpty) {
          setValidationError(`Please fill out required field: "${field.label || field.key}"`);
          return;
        }
      }
    }

    setValidationError(null);
    onConfirm(cardType, formData);
  };

  const cardIcon = useMemo(() => getCardIcon(cardType), [cardType]);

  const customAction =
    !isConfirmed && onSaveDraft ? (
      <Button
        variant="outlined"
        size="small"
        onClick={handleSaveDraft}
        disabled={isSavingDraft || isConfirming}
        data-testid={`save-draft-btn-${cardType}`}
        sx={{
          textTransform: 'none',
          borderRadius: 2,
          fontWeight: 600,
          borderColor: '#CBD5E1',
          color: '#475569',
          '&:hover': {
            borderColor: '#8A4BFF',
            color: '#8A4BFF',
          },
        }}
      >
        {isSavingDraft ? 'Saving...' : 'Save Draft'}
      </Button>
    ) : null;

  return (
    <AiCardBase
      cardType={cardType}
      label={label || cardType}
      isConfirmed={isConfirmed}
      isConfirming={isConfirming}
      onConfirm={handleConfirm}
      isEditedByDoctor={isEditedByDoctor}
      draftSavedAt={draftSavedAt}
      icon={cardIcon}
      customAction={customAction}
    >
      <Box sx={{ py: 1 }}>
        {validationError && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {validationError}
          </Alert>
        )}

        {/* Dynamic Fields Grid */}
        <Grid container spacing={2}>
          {fields.map((field) => {
            if (!shouldShowField(field, formData, formData)) return null;
            if (field.type === 'hidden') return null;

            const gridCols = getFieldGridColumns(field);

            return (
              <Grid
                size={gridCols}
                key={field.key}
              >
                <DynamicFieldRenderer
                  field={field}
                  value={formData[field.key]}
                  onChange={(val) => handleFieldChange(field.key, val)}
                  scopeValues={formData}
                  rootValues={formData}
                  disabled={isConfirmed}
                  cardType={cardType}
                />
              </Grid>
            );
          })}
        </Grid>

        {/* Read-only Data Section (Historical readings, ideal weight gain, etc.) */}
        {readOnlyData && (
          <ReadOnlyDataSection cardType={cardType} readOnlyData={readOnlyData} />
        )}
      </Box>
    </AiCardBase>
  );
};

export default DynamicAiCard;
